import { parseSearchFiltersToUrl } from '~/apps/pu-links/infrastructure/searchUrlHelpers'
import { getTransformObject } from '~/common/photos/transforms'
import type { Logger } from '~/composables/useGtmLogRequest'
import { getLangFallbackUrl } from '~/lang/utils/langfetch'
import { getWKTPointFromGeoPoint } from '~/utility/geo/GeoLocation'
import { getPointFromStringOrDefault } from '~/utility/geo/latLng'
import { keysToCamel } from '~/utility/toCamel'
import {
  CampsiteMapsSearchResult,
  MapSearchCampsitesResults,
  SearchFilters,
  campsiteMapPopupSchema,
} from '../schemas'
import { formatBounds } from './bounds'

export class FetchCampsiteMapSearchService {
  async search(
    filters: SearchFilters,
    langCode: string,
    logger: Logger,
    fetchOptions?: any,
  ): Promise<MapSearchCampsitesResults | undefined> {
    const params = this.filtersToParams(filters)
    const fieldsToShow = [
      'cheapest_lead_price',
      'id',
      'lead_price',
      'point',
      'slug',
    ]
    // const gtmLogRequest = useGtmLogRequest(
    //   'FetchCampsiteMapSearchService-search',
    //   logger,
    // )
    // gtmLogRequest.markStart()
    const url = getLangFallbackUrl(
      langCode,
      `/search2/postgres-nonprice/?fields=${fieldsToShow.join(
        ',',
      )}&sort=magic&facet_counts=none&seo=0&${params.toString()}`,
    )
    const response: any = await $fetch(url, fetchOptions)
    // gtmLogRequest.markEnd()
    if (!response) {
      // return nothing early if response is empty, otherwise it will throw an error later on
      return
    }
    return {
      campsites: this.formatResults(keysToCamel(response.results)),
      pagination: {
        currentPage: Number(response.page),
        totalPages: response.num_pages,
        totalCount: response.count,
      },
      bounds: response.bounding_box
        ? formatBounds(response.bounding_box)
        : null,
      title: response.title,
      heading: response.heading,
      bookable: !!response.bookable,
      category: response.category,
      count: response.bookable_count,
      path: response.path,
      point: response.point,
      leadPrice: response.lead_price,
      cheapestLeadPrice: response.cheapest_lead_price,
      sortOptions: response.sort_options,
      sortOption: response.sort || response.sort_field,
      maxPrice: response.max_price,
      within: response.within,
    }
  }

  private formatResults(results: any[]): CampsiteMapsSearchResult[] {
    return results.map((campsite) => {
      const point = getWKTPointFromGeoPoint(
        getPointFromStringOrDefault(campsite.point),
      )
      const { lat, lng } = campsite.point
      return {
        id: campsite.id.toString(),
        slug: campsite.slug,
        price: campsite.cheapestLeadPrice
          ? campsite.cheapestLeadPrice[0]
          : undefined,
        point,
        lat,
        lng,
      }
    })
  }

  // tslint:disable-next-line cognitive-complexity
  private filtersToParams(filters: SearchFilters) {
    const params = new URLSearchParams([])
    for (const param of parseSearchFiltersToUrl({
      ...filters,
      // limit: SEARCH_RESULTS_LIMIT,
    })) {
      params.append(param.filterName, param.filterValue)
    }
    if (params.get('bounds')) {
      // remove bounds if path
      if (params.get('path')) {
        params.delete('bounds')
      }
    }
    // only full bookable
    params.append('bookable', '1')
    return params
  }

  async fetchCampsiteBySlug(slug, langCode, logger: Logger) {
    const gtmLogRequest = useGtmLogRequest(
      'FetchCampsiteMapSearchService-fetchCampsiteBySlug',
      logger,
    )
    gtmLogRequest.markStart()
    const campsite: any = await $fetch(
      getLangFallbackUrl(langCode, `/_/campsite-details/slim/${slug}/`),
    )
    gtmLogRequest.markEnd()
    return campsiteMapPopupSchema.parse({
      ...campsite,
      bookable: campsite.isBookable,
      hierarchy: campsite.hierarchyTextShort,
      photo: campsite.primaryPhoto
        ? {
            url: getTransformObject(
              'map',
              campsite.primaryPhoto.url.master_image,
            ),
            caption: campsite.primaryPhoto?.caption || '',
          }
        : null,
      price: campsite.leadPrice ? campsite.leadPrice : undefined,
      status: campsite.isBookable ? 'bookable' : 'free',
    })
  }
}

export function getCampsiteBySlug(slug, langCode, logger: Logger) {
  const service = new FetchCampsiteMapSearchService()
  return service.fetchCampsiteBySlug(slug, langCode, logger)
}
